import { useEffect, useState } from "react";
import LETTER_BATCHES_QUERY from "react-migration/domains/sites/apollo/letters-api/queries/letterBatchesQuery.gql";
import { LandTechEndpoints, routedClient } from "react-migration/lib";
import { useDebounce } from "usehooks-ts";

import { useLazyQuery } from "@apollo/client";

import {
  LetterBatch,
  LetterBatchesQueryResponseData,
  LetterBatchesQueryVariables,
  RemoteFile,
} from "../../typings";

export type LoadLetterBatchesProps = {
  name?: string;
  fromDate?: string;
  toDate?: string;
};

type LoadLetterBatchesData = {
  loading: boolean;
  error?: string;
  letterBatches: LetterBatch[];
  totalCount: number;
};

export function useLoadLetterBatches({
  name,
  fromDate,
  toDate,
}: LoadLetterBatchesProps): LoadLetterBatchesData {
  const [fetchLetterBatches, { loading, data: batchesData, error }] = useLazyQuery<
    LetterBatchesQueryResponseData,
    LetterBatchesQueryVariables
  >(LETTER_BATCHES_QUERY, {
    context: { endpoint: LandTechEndpoints.Letters },
    client: routedClient,
    fetchPolicy: "network-only",
  });

  const [letterBatches, setLetterBatches] = useState<LetterBatch[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const debouncedName = useDebounce(name, 500);
  const debouncedFromDate = useDebounce(fromDate, 500);
  const debouncedToDate = useDebounce(toDate, 500);

  useEffect(() => {
    fetchLetterBatches({
      variables: {
        letterBatchesQueryInput: {
          name: debouncedName,
          from_date: debouncedFromDate,
          to_date: debouncedToDate,
        },
      },
    });
  }, [debouncedName, debouncedFromDate, debouncedToDate, fetchLetterBatches]);

  useEffect(() => {
    if (!loading && batchesData) {
      const newLetterBatches = batchesData.letterBatches.map(
        ({ id, name, status, created_at, updated_at, letters, total_price }) => ({
          id,
          name,
          status,
          created: created_at,
          updated: updated_at,
          lettersCount: letters.length,
          letterPdfs: letters.reduce<RemoteFile[]>(
            (
              result,
              { signed_pdf_url, recipient_address_line_1, recipient_city, recipient_name, id }
            ) => {
              if (signed_pdf_url) {
                result.push({
                  url: signed_pdf_url,
                  name: `${recipient_address_line_1}, ${recipient_city} - ${recipient_name} - ${id}.pdf`,
                });
              }
              return result;
            },
            []
          ),
          price: total_price / 100,
        })
      );
      setLetterBatches(newLetterBatches);
      setTotalCount(batchesData?.letterBatches.length);
    }
  }, [batchesData, loading]);

  if (error) {
    return {
      loading,
      error: error.message,
      letterBatches: [],
      totalCount: 0,
    };
  }

  return {
    loading,
    letterBatches,
    totalCount,
  };
}
